/**
 * Shared A4 quotation layout tokens for the downloadable PDF.
 * html2canvas captures element boxes — @page / @media print margins do not apply.
 */

import { BOM_PRINT_FONT, BOM_PRINT_LOGO_HEIGHT_PX } from "@/lib/bomPrintDesign";
import { PROPOSAL_BG } from "@/lib/proposalTheme";
import type { CSSProperties } from "react";

/** 96dpi mm → px */
export function mmToPx(mm: number): number {
  return (mm * 96) / 25.4;
}

export const PDF_A4_WIDTH_MM = 210;
export const PDF_A4_HEIGHT_MM = 297;
export const PDF_QUOTATION_MARGIN_X_MM = 15;
export const PDF_QUOTATION_MARGIN_Y_MM = 14;

export const PDF_A4_WIDTH_PX = Math.round(mmToPx(PDF_A4_WIDTH_MM)); // ~794
export const PDF_A4_HEIGHT_PX = Math.round(mmToPx(PDF_A4_HEIGHT_MM)); // ~1123
export const PDF_QUOTATION_MARGIN_X_PX = Math.round(mmToPx(PDF_QUOTATION_MARGIN_X_MM)); // ~57
export const PDF_QUOTATION_MARGIN_Y_PX = Math.round(mmToPx(PDF_QUOTATION_MARGIN_Y_MM)); // ~53
/** Usable content width inside horizontal margins (~180mm). */
export const PDF_QUOTATION_CONTENT_WIDTH_PX =
  PDF_A4_WIDTH_PX - PDF_QUOTATION_MARGIN_X_PX * 2; // ~680

/**
 * Body font for post-cover quotation pages — same stack as Project BOM.
 * Cover keeps PROPOSAL_COPY_FONT (identical token).
 */
export const PDF_QUOTATION_BODY_FONT = BOM_PRINT_FONT;

/**
 * Header logo — BOM restrained height (~28px). Width follows aspect ratio.
 */
export const PDF_QUOTATION_LOGO_HEIGHT_PX = BOM_PRINT_LOGO_HEIGHT_PX;
export const PDF_QUOTATION_LOGO_WIDTH_PX = Math.round(BOM_PRINT_LOGO_HEIGHT_PX * (88 / 37));
export const PDF_QUOTATION_LOGO_MAX_WIDTH_PX = PDF_QUOTATION_LOGO_WIDTH_PX + 8;

/** Closing-page logo — slightly larger than header, still restrained. */
export const PDF_CLOSING_LOGO_HEIGHT_PX = 40;
export const PDF_CLOSING_LOGO_WIDTH_PX = Math.round(PDF_CLOSING_LOGO_HEIGHT_PX * (110 / 46));
export const PDF_CLOSING_LOGO_MAX_WIDTH_PX = PDF_CLOSING_LOGO_WIDTH_PX + 10;

export const PDF_QUOTATION_PAGE_SHELL_CLASS = "dtools-pdf-quotation-page-shell";
export const PDF_QUOTATION_LOGO_CLASS = "dtools-pdf-quotation-logo";
export const PDF_CLOSING_LOGO_CLASS = "dtools-pdf-closing-logo";

/** Warm cream — same as cover `PROPOSAL_BG` for internal quotation pages. */
export const PDF_QUOTATION_PAGE_BG = PROPOSAL_BG;

export const PDF_QUOTATION_PAGE_SHELL_STYLE: CSSProperties = {
  boxSizing: "border-box",
  width: `${PDF_A4_WIDTH_PX}px`,
  minHeight: `${PDF_A4_HEIGHT_PX}px`,
  padding: `${PDF_QUOTATION_MARGIN_Y_PX}px ${PDF_QUOTATION_MARGIN_X_PX}px`,
  margin: 0,
  background: PDF_QUOTATION_PAGE_BG,
  overflow: "hidden",
};

/** Inline logo constraints — height-led like BOM (must not rely on @media print). */
export const PDF_QUOTATION_LOGO_STYLE: CSSProperties = {
  display: "block",
  height: `${PDF_QUOTATION_LOGO_HEIGHT_PX}px`,
  width: "auto",
  maxWidth: `${PDF_QUOTATION_LOGO_MAX_WIDTH_PX}px`,
  objectFit: "contain",
  flex: "none",
  flexShrink: 0,
};

export const PDF_CLOSING_LOGO_STYLE: CSSProperties = {
  display: "block",
  height: `${PDF_CLOSING_LOGO_HEIGHT_PX}px`,
  width: "auto",
  maxWidth: `${PDF_CLOSING_LOGO_MAX_WIDTH_PX}px`,
  objectFit: "contain",
  flex: "none",
  marginLeft: "auto",
  marginRight: "auto",
};

/** Apply shell styles to a pagination-created DOM node. */
export function applyPdfQuotationPageShellStyles(page: HTMLElement): void {
  page.classList.add(PDF_QUOTATION_PAGE_SHELL_CLASS, "pdf-print-page");
  page.style.boxSizing = "border-box";
  page.style.width = `${PDF_A4_WIDTH_PX}px`;
  page.style.minHeight = `${PDF_A4_HEIGHT_PX}px`;
  page.style.padding = `${PDF_QUOTATION_MARGIN_Y_PX}px ${PDF_QUOTATION_MARGIN_X_PX}px`;
  page.style.margin = "0";
  page.style.background = PDF_QUOTATION_PAGE_BG;
  page.style.overflow = "hidden";
}

export function applyPdfQuotationLogoStyles(img: HTMLImageElement): void {
  img.classList.add(PDF_QUOTATION_LOGO_CLASS);
  img.style.display = "block";
  img.style.height = `${PDF_QUOTATION_LOGO_HEIGHT_PX}px`;
  img.style.width = "auto";
  img.style.maxWidth = `${PDF_QUOTATION_LOGO_MAX_WIDTH_PX}px`;
  img.style.objectFit = "contain";
  img.style.flex = "none";
  img.style.flexShrink = "0";
}
